/**
 * Main Lobby Component for Lobby2.0
 * ULTRA PERFORMANCE OPTIMIZED: Zero rerenders on view switching
 * Includes conditional tournament navigation rendering
 */

import { memo } from 'react';
import { styled } from 'styled-components';

import { ViewSwitcher, Navigation, StatusBar } from 'features/navigation';
import { SearchSection } from 'features/search';
import SettingsModal from 'features/settings';
import { useBackToGameToast } from 'features/games';
import { useHideNavFooterOnScroollMobile } from 'shared/hooks/useHideNavFooterOnScroollMobile';

import LobbyJackpotStatistics from './LobbyJackpotStatistics';

import { useOpenType } from '../../core/providers/OpenTypeContext';
import { useAppStore } from '../../store/app/appStore';

const StatusBarWrapper = styled.div<{ $openType: 'fullscreen' | 'ingame'; }>`
  position: ${({ $openType }) => $openType === 'ingame' ? 'absolute' : 'fixed'};
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 100;
  /* Hardware acceleration for smooth performance */
  transition: transform 0.25s ease;
  backface-visibility: hidden;
`;

const Lobby: React.FC = () => {
  const isSearchActive = useAppStore((state) => state.isSearchActive);
  const sections = useAppStore((state) => state.sections);
  const { openType } = useOpenType();
  const isNavFooterHidden = useHideNavFooterOnScroollMobile();

  // Initialize back to game toast
  useBackToGameToast();

  // Check if sections are ready - no need to check settingsRequestDone since sections can't exist without it
  const areSectionsReady = sections && sections.length > 0;

  return (
    <>
      {/* <InGameHeader /> */}
      <ViewSwitcher showTournamentNavigation={false} />

      {/* Navigation Rendering - Always mounted to prevent re-renders, hidden with CSS when search is active */}
      {areSectionsReady && (
        <Navigation style={{ visibility: isSearchActive ? 'hidden' : 'visible' }} hidden={isNavFooterHidden} />
      )}

      {/* StatusBar */}
      {openType !== 'ingame' && areSectionsReady && (
        <StatusBarWrapper $openType={openType}>
          <StatusBar />
        </StatusBarWrapper>
      )}
      
      <SearchSection />
      <SettingsModal />

      {/* Jackpot statistics drawer (portals to document.body). Mounted here — deliberately far
          from the jackpot section panels — so it is the only subscriber to the drawer's open
          state: opening it cannot re-render the memoised panels that drive the odometers. */}
      <LobbyJackpotStatistics />
    </>
  );
};

// This component never rerenders after initial mount!
export default memo(Lobby);